import type { TransportResponse } from '@mangovoice/api-client'
import type { MangoApiClient } from '@mangovoice/mango-api-client'

// ---------------------------------------------------------------------------
// API response types (re-exported from @mangovoice/api-client for compat)
// ---------------------------------------------------------------------------

export type ApiResponse<T, _E = unknown> = TransportResponse<T>

export interface ApiError {
  type?: string
  code?: string
  message?: string
  errors?: Array<{
    code: string
    detail: string
    attr: string | null
  }>
}

// ---------------------------------------------------------------------------
// HTTP client interface (provided by MangoApiClient['soundClips']['transport'])
// ---------------------------------------------------------------------------

export type SoundClipApiClient = MangoApiClient['soundClips']['transport']

// ---------------------------------------------------------------------------
// Domain types
// ---------------------------------------------------------------------------

export type FeatureCode =
  0 | 100 | 101 | 102 | 103 | 104 | 105 | 106 | 107 | 108 | 109

export interface SoundClip {
  id: number
  name: string
  feature_code: FeatureCode
  location: string
  /**
   * Presigned playback URL added by mango-api PR #1365. The
   * mango-api → music-service wrapper is expected to keep this populated by
   * regenerating a presigned URL from music-service's `location`, even
   * though music-service itself returns `file_encoded_base64` on detail.
   */
  file_url: string | null
  pbx_id: number
  pbx_uuid: string
  /**
   * Stable UUID for the sound clip. music-service exposes this on every
   * sound-clip serializer; the mango-api wrapper should surface it.
   * Optional until the wrapper ships — new code should prefer `uuid` over
   * `id` when available.
   */
  uuid?: string
  created_at: string
  updated_at: string
}

export interface PaginatedResponse<T> {
  count: number
  next: string | null
  previous: string | null
  results: T[]
}

// ---------------------------------------------------------------------------
// Write payloads
// ---------------------------------------------------------------------------

export interface CreateSoundClipPayload {
  name: string
  feature_code: FeatureCode
  pbx_id: number
  audio_file: File
}

export interface UpdateSoundClipPayload {
  name?: string
  feature_code?: FeatureCode
  audio_file?: File
}

// ---------------------------------------------------------------------------
// Pagination
// ---------------------------------------------------------------------------

export interface SoundClipListParams {
  pbxId: number
  limit?: number
  offset?: number
  search?: string
  /** DRF ordering, e.g. `name` or `-updated_at`. */
  ordering?: string
}

export interface SoundClipListResult {
  results: SoundClip[]
  totalCount: number
}

// ---------------------------------------------------------------------------
// Sound Clip API interface (all calls go through mango-api)
// ---------------------------------------------------------------------------

export interface SoundClipApi {
  getSoundClips(params: SoundClipListParams): Promise<SoundClipListResult>
  getSoundClip(id: number): Promise<SoundClip>
  createSoundClip(payload: CreateSoundClipPayload): Promise<SoundClip>
  updateSoundClip(
    id: number,
    payload: UpdateSoundClipPayload,
  ): Promise<SoundClip>
  deleteSoundClip(id: number): Promise<void>
}

export const FEATURE_CODE_OPTIONS: { value: FeatureCode; label: string }[] = [
  { value: 0, label: 'None' },
  { value: 100, label: '*100' },
  { value: 101, label: '*101' },
  { value: 102, label: '*102' },
  { value: 103, label: '*103' },
  { value: 104, label: '*104' },
  { value: 105, label: '*105' },
  { value: 106, label: '*106' },
  { value: 107, label: '*107' },
  { value: 108, label: '*108' },
  { value: 109, label: '*109' },
]
